import { TASK_KIND_LABELS, type TaskSummary } from "@dentacore/shared-types";

import styles from "./tasks.module.css";

/** The colour each automatic kind is drawn in; manual types share the quiet one. */
const KIND_CLASS: Record<string, string | undefined> = {
  LOW_STOCK: styles.kindStock,
  UNPAID_BILL: styles.kindBill,
  RECALL_DUE: styles.kindRecall,
  VISIT_NOT_CLOSED: styles.kindVisit,
};

/** What kind of task it is: an automatic kind, or the clinic's manual type. */
export function KindChip({ task }: { task: TaskSummary }): React.JSX.Element {
  const label = task.kind !== null ? TASK_KIND_LABELS[task.kind] : (task.type?.name ?? "Task");
  const tone = task.kind === null ? undefined : KIND_CLASS[task.kind];
  return (
    <span className={tone === undefined ? styles.kind : `${styles.kind} ${tone}`}>{label}</span>
  );
}

export function SourceBadge({ task }: { task: TaskSummary }): React.JSX.Element {
  return (
    <span
      className={styles.source}
      title={
        task.source === "AUTO"
          ? "Created by DentaCore"
          : task.source === "WORKFLOW"
            ? "Created by a workflow"
            : "Created by a person"
      }
    >
      {task.source === "AUTO" ? "Auto" : task.source === "WORKFLOW" ? "Workflow" : "Manual"}
    </span>
  );
}

/** Two letters for a person or a role, as the prototype draws them. */
export function initials(name: string): string {
  const words = name
    .replace(/[^\p{L}\s]/gu, " ")
    .trim()
    .split(/\s+/);
  const letters =
    words.length > 1
      ? `${words[0]?.[0] ?? ""}${words[1]?.[0] ?? ""}`
      : (words[0] ?? "").slice(0, 2);
  return letters.toUpperCase() || "?";
}

/** Who holds it, or the role it waits on, unclaimed (TK-D10). */
export function Holder({ task }: { task: TaskSummary }): React.JSX.Element {
  if (task.assignee !== null)
    return (
      <span className={styles.holder}>
        <span className={styles.avatar} aria-hidden="true">
          {initials(task.assignee.name)}
        </span>
        <span className={styles.ellipsis}>{task.assignee.name}</span>
      </span>
    );
  return (
    <span className={styles.holder}>
      <span className={`${styles.avatar} ${styles.avatarRole}`} aria-hidden="true">
        ?
      </span>
      <span className={styles.ellipsis}>
        {task.role?.name ?? "Nobody"} <span className={styles.unclaimed}>· unclaimed</span>
      </span>
    </span>
  );
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** "Today 10:15", "Tomorrow 08:00", "Mon 26 Oct, 08:00" — on this browser's clock. */
export function when(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  const time = at.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const dayOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((dayOf(at) - dayOf(now)) / DAY_MS);
  if (diff === 0) return `Today ${time}`;
  if (diff === -1) return `Yesterday ${time}`;
  if (diff === 1) return `Tomorrow ${time}`;
  const day = at.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  return `${day}, ${time}`;
}

/** The right-hand cell: how it ended, how long it sleeps, when it is due, or when it was raised. */
export function WhenCell({ task, now }: { task: TaskSummary; now: Date }): React.JSX.Element {
  if (task.status !== "OPEN") {
    const [label, tone] =
      task.status === "RESOLVED"
        ? ["Resolved", styles.outcomeDone]
        : task.status === "AUTO_CLOSED"
          ? ["Closed by DentaCore", styles.outcomeAuto]
          : ["Closed", styles.outcomeClosed];
    return (
      <span className={styles.when}>
        <span className={`${styles.outcome} ${tone ?? ""}`}>{label}</span>
        <br />
        {task.doneAt === null ? "" : when(task.doneAt, now)}
      </span>
    );
  }
  if (task.snoozedUntil !== null && new Date(task.snoozedUntil) > now)
    return (
      <span className={`${styles.when} ${styles.snoozed}`}>
        Snoozed until
        <br />
        {when(task.snoozedUntil, now)}
      </span>
    );
  if (task.dueAt !== null)
    return new Date(task.dueAt) < now ? (
      <span className={`${styles.when} ${styles.late}`}>
        Overdue
        <br />
        {when(task.dueAt, now)}
      </span>
    ) : (
      <span className={styles.when}>
        Due
        <br />
        {when(task.dueAt, now)}
      </span>
    );
  return (
    <span className={styles.when}>
      Created
      <br />
      {when(task.createdAt, now)}
    </span>
  );
}

/** Where the task's record lives in the product (TK-D37). Null for a task about nothing. */
export function recordLink(task: TaskSummary): { to: string; label: string } | null {
  const subject = task.subject;
  if (subject === null) return null;
  switch (subject.kind) {
    case "item":
      return { to: `/inventory?item=${subject.id}`, label: "Open in Inventory" };
    case "patient":
      return { to: `/patients/${subject.id}`, label: "Open the patient file" };
    case "invoice":
      return subject.patientId === null
        ? null
        : { to: `/patients/${subject.patientId}?tab=Billing`, label: "Open billing" };
    case "appointment":
      return { to: `/calendar?appointment=${subject.id}`, label: "Open in Calendar" };
  }
}

/** What the record is, in a word, for the card beside the task. */
export const RECORD_KIND: Record<string, string> = {
  item: "Inventory item",
  patient: "Patient",
  invoice: "Bill",
  appointment: "Appointment",
};

/** A visit's subject label is its start time; everything else is already words. */
export function recordName(task: TaskSummary): string {
  const subject = task.subject;
  if (subject === null) return "";
  return subject.kind === "appointment" ? when(subject.label) : subject.label;
}
